import { format, isValid } from "date-fns";
import { Bell, FileSearch2, FileText, Library, LucideIcon, Mail, SquareKanban, Zap } from "lucide-react";
import { rotuloDoProduto } from "../../components/Automacoes/produtos";
import { INotificationOrigin } from "../../_services/interface/notifications.interface";

/**
 * Linha de origem das notificações — GEN-4080 (sino e detalhe).
 *
 * "Certidões · via Alerta “CADIN” · há 3 h": a pessoa sabe de QUAL produto é o aviso e QUAL regra o
 * mandou sem abrir nada. Sem isso, um aviso de alerta e um de automação eram indistinguíveis, e a única
 * forma de achar a regra para desligá-la era procurar na lista inteira.
 *
 * Funções puras, sem React, para serem testadas e reaproveitadas no sino, na lista e no detalhe.
 */

const juntar = (partes: Array<string | null | undefined>) => {
  const limpas: string[] = [];
  for (const parte of partes) {
    // Sem repetir o mesmo trecho em seguida ("Base Cadastral · Base Cadastral").
    if (parte && parte !== limpas[limpas.length - 1]) limpas.push(parte);
  }
  return limpas.join(" · ");
};

/**
 * "Certidões · via Alerta “CADIN”", "Caixa Postal · via Automação “Intimações”", "Base Cadastral".
 * Vazio quando não há o que dizer (notificação antiga ou de origem desconhecida sem produto).
 *
 * Sem o nome da regra, não se afirma o "via": o backend só manda `tipo` com o nome quando o aviso é de
 * UMA regra — sem ele, "via Alerta" seria palpite. Fica o produto (ou, sem produto, só "Alerta").
 */
export const textoDaOrigem = (origem: INotificationOrigin | null | undefined): string => {
  if (!origem) return "";
  const produto = rotuloDoProduto(origem.produto);
  const nome = origem.nome?.trim();
  switch (origem.tipo) {
    case "alerta":
      return nome ? juntar([produto, `via Alerta “${nome}”`]) : produto ?? "Alerta";
    case "automacao":
      return nome ? juntar([produto, `via Automação “${nome}”`]) : produto ?? "Automação";
    case "base_cadastral":
      return juntar(["Base Cadastral"]);
    default:
      return juntar([produto]);
  }
};

const paraData = (valor: Date | string | number | null | undefined): Date | null => {
  if (valor === null || valor === undefined || valor === "") return null;
  const data = valor instanceof Date ? valor : new Date(valor);
  return isValid(data) ? data : null;
};

/** "14/10/2026 às 22:35" — o formato de data completa do sino (tooltip) e do detalhe. */
export const dataCompleta = (valor: Date | string | number | null | undefined): string => {
  const data = paraData(valor);
  return data ? format(data, "dd/MM/yyyy 'às' HH:mm") : "";
};

/**
 * Tempo relativo curto: "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias"; de uma semana para trás, a
 * data ("07/10/2026"). Curto de propósito: divide a linha com o produto e a regra.
 */
export const tempoRelativo = (valor: Date | string | number | null | undefined, agora: Date = new Date()): string => {
  const data = paraData(valor);
  if (!data) return "";
  const minutos = Math.floor((agora.getTime() - data.getTime()) / 60000);
  if (minutos < 1) return "agora";
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return "ontem";
  if (dias < 7) return `há ${dias} dias`;
  return format(data, "dd/MM/yyyy");
};

/** Valor do `dateTime` do `<time>` (ISO 8601); vazio para data inválida. */
export const dataIso = (valor: Date | string | number | null | undefined): string => {
  const data = paraData(valor);
  return data ? data.toISOString() : "";
};

/**
 * Ícone do produto da notificação — os MESMOS da sidebar (Certidões, Caixa Postal, Conta Corrente,
 * Kanban 2.0, Base Cadastral), para o aviso apontar visualmente para o módulo de onde veio.
 */
const ICONE_DO_PRODUTO: Record<string, LucideIcon> = {
  certificate: FileText,
  dte: Mail,
  tax_current_account: FileSearch2,
  divida_ativa: FileSearch2,
  cnpj_situation: FileSearch2,
  ie_situation: FileSearch2,
  status: FileSearch2,
  kanban2: SquareKanban,
  registry: Library,
};

export const iconeDaOrigem = (origem: INotificationOrigin | null | undefined): LucideIcon => {
  const doProduto = origem?.produto ? ICONE_DO_PRODUTO[origem.produto] : undefined;
  if (doProduto) return doProduto;
  if (origem?.tipo === "base_cadastral") return Library;
  if (origem?.tipo === "automacao") return Zap;
  return Bell;
};
